import { useInfiniteQuery, useMutation, useQuery, useQueryCache } from "@pinia/colada";
import {
	type CreateStation,
	HardwareConfigContext,
	type Station,
	type UpdateStation,
	useApi,
	UserScope,
} from "@soft-db/monitoring-api-client";
import { storeToRefs } from "pinia";
import { TaggedUnion } from "type-fest";
import { usePermissionStore } from "../stores/permission";
import { useQueryManager } from "../stores/query-manager";
import { useScopeStore } from "../stores/scope";
import {
	composeCacheEffects,
	QueryOptions,
	removeUndefinedFromListQuery,
	useCachedEntries,
	useInvalidator,
	useQueryEnabled,
	useScopeDataUpdater,
	useScopedQuery,
} from "./query";
import { sitesKeys } from "./site-query";

// --- Keys ---

export const stationsKeys = {
	root: ["stations"] as const,

	/**
	 * Root to easily invalidate all list queries,
	 * without invalidating individual site queries
	 */
	list: () => [...stationsKeys.root, "list"] as const,

	/**
	 * The default collection for the user
	 */
	byContext: () => [...stationsKeys.list(), "context"],
	byFavorites: () => [...stationsKeys.list(), "favorites"],

	byDist: (dist: string) => [...stationsKeys.list(), "dist", dist] as const,
	bySpace: (space: string) => [...stationsKeys.list(), "space", space] as const,

	byId: (id: string) => [...stationsKeys.root, "id", id] as const,
};

export const hardwareConfigKeys = {
	root: ["hardware-configs"] as const,

	byContext: (context: HardwareConfigContext) => [
		...hardwareConfigKeys.root,
		JSON.stringify(context),
	],

	latest: (context: HardwareConfigContext) => [hardwareConfigKeys.byContext(context), "latest"],
	history: (context: HardwareConfigContext) => [hardwareConfigKeys.byContext(context), "history"],
};

function useStationEnabled(...args: MaybeRefOrGetter<string | boolean>[]) {
	const { hasPermission } = usePermissionStore();

	return useQueryEnabled(...args, () => hasPermission("station.get"));
}

// --- Cache effects ---

function useUpdateCacheById() {
	const update = useScopeDataUpdater();

	return (station: Station) => {
		update(stationsKeys.byId(station.id), station);
	};
}

function useInvalidateListCache() {
	const invalidate = useInvalidator();

	return (station: Station) => {
		invalidate({
			keys: [stationsKeys.list(), sitesKeys.root],
			otherScopeKeys: [stationsKeys.byId(station.id)],
		});
	};
}

function useStationCacheEffects() {
	return composeCacheEffects(useUpdateCacheById(), useInvalidateListCache());
}

// --- Queries ---

export function useStation(id: MaybeRefOrGetter<string>) {
	const api = useApi().station();

	const enabled = useStationEnabled(id);
	const { data: station, ...rest } = useScopedQuery({
		key: () => stationsKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled,
	});

	return {
		station,
		...rest,
		// For compatibility
		data: station,
	};
}

export function useQuickAccessStations(_isFavorite: MaybeRefOrGetter<boolean>) {
	const api = useApi().station();

	const { scope } = storeToRefs(useScopeStore());

	const isFavorite = computed(() => {
		if (scope.value.type === "space") {
			return false;
		}

		return toValue(_isFavorite);
	});

	const enabled = useStationEnabled(() => scope.value.type !== "none");

	const { data: stations, ...rest } = useScopedQuery({
		key: () => (isFavorite.value ? stationsKeys.byFavorites() : stationsKeys.byContext()),
		query: () => (isFavorite.value ? api.fetchFavorites() : api.fetchContext()),
		enabled,
	});

	return {
		stations,
		...rest,
		// For compatibility
		data: stations,
	};
}

export function useStations(opts?: QueryOptions) {
	const api = useApi().station();

	const enabled = useStationEnabled(opts?.enabled || true);

	const { data: stations, ...rest } = useScopedQuery({
		key: stationsKeys.byContext(),
		query: () => api.fetchContext(),
		enabled,
	});

	return {
		stations: removeUndefinedFromListQuery(stations),
		data: stations,
		...rest,
	};
}

export function useSpaceStations(id: MaybeRefOrGetter<string>) {
	const api = useApi().station();

	const { data: stations, ...rest } = useQuery({
		key: () => stationsKeys.bySpace(toValue(id)),
		query: () => api.fetchSpace(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return { stations: stations, ...rest };
}

export function useDistributorStations(id: MaybeRefOrGetter<string>) {
	const api = useApi().station();

	const { data: stations, ...rest } = useQuery({
		key: () => stationsKeys.byDist(toValue(id)),
		query: () => api.fetchDist(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return { stations: stations, ...rest };
}

export function useScopeStations(scopeRef: MaybeRefOrGetter<UserScope>) {
	const api = useApi().station();

	const { data: stations, ...rest } = useQuery({
		key: () => {
			const scope = toValue(scopeRef);

			switch (scope.type) {
				case "distributor":
					return stationsKeys.byDist(scope.id);
				case "space":
					return stationsKeys.bySpace(scope.id);
				case "none":
					return ["/dev/null"];
			}
		},
		query: async () => {
			const scope = toValue(scopeRef);
			switch (scope.type) {
				case "distributor":
					return api.fetchDist(scope.id);
				case "space":
					return api.fetchSpace(scope.id);
				case "none":
					return [];
			}
		},
		enabled: useQueryEnabled(() => !!toValue(scopeRef)),
	});

	return { stations: stations, ...rest };
}

export function useCachedStations() {
	return useCachedEntries<Station>(stationsKeys.root);
}

export function useLatestHardwareConfig(context: MaybeRefOrGetter<HardwareConfigContext>) {
	const api = useApi().station();

	const { data: hardwareConfig, ...rest } = useQuery({
		key: () => hardwareConfigKeys.latest(toValue(context)),
		query: () => api.getLatestHarwdareConfig(toValue(context)),
	});

	return {
		hardwareConfig,
		...rest,
	};
}

export function useHardwareConfigHistory(
	context: MaybeRefOrGetter<HardwareConfigContext>,
	opts: {
		enabled?: MaybeRefOrGetter<boolean>;
		pageSize?: number;
	}
) {
	const api = useApi().station();

	const { data: hardwareConfigs, ...rest } = useInfiniteQuery({
		key: () => hardwareConfigKeys.history(toValue(context)),
		initialPageParam: 0,
		query: ({ pageParam }) =>
			api.searchHardwareConfig(toValue(context), {
				pageIndex: pageParam,
				pageSize: opts.pageSize ?? 5,
			}),
		getNextPageParam(lastPage, _, lastPageParam) {
			return lastPage.hasMore ? lastPageParam + 1 : null;
		},

		enabled: opts.enabled,
	});

	return {
		hardwareConfigs,
		...rest,
	};
}

// --- Mutations ---

export function useStationCreation() {
	const api = useApi().station();

	return useMutation({
		mutation: (station: CreateStation) => api.create(station),
		onSuccess: useStationCacheEffects(),
	});
}

export function useLinkStationSite() {
	const api = useApi().station();

	const cache = useQueryCache();

	return useMutation({
		mutation: ({ id, site }: { id: string; site: string }) => api.linkSite(id, site),
		onSuccess: composeCacheEffects(useStationCacheEffects(), () => {
			cache.invalidateQueries({ key: sitesKeys.root });
		}),
	});
}

export function useUnlinkStationSite() {
	const api = useApi().station();

	const cache = useQueryCache();

	return useMutation({
		mutation: (id: string) => api.unlinkSite(id),
		onSuccess: composeCacheEffects(useStationCacheEffects(), () => {
			cache.invalidateQueries({ key: sitesKeys.root });
		}),
	});
}

export function useTransferStation() {
	const api = useApi().station();

	return useMutation({
		mutation: ({ id, dist }: { id: string; dist: string | null }) => api.transfer(id, dist),
		onSuccess: useStationCacheEffects(),
	});
}

export function useUpdateStation() {
	const api = useApi().station();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: UpdateStation }) => api.update(id, data),
		onSuccess: useStationCacheEffects(),
	});
}

export type StationMutation = TaggedUnion<
	"type",
	{
		Update: { data: UpdateStation };
		LinkSite: { site: string };
		UnlinkSite: {};
	}
> & { id: string };

export function useStationMutationsExecutor() {
	const updateMut = useUpdateStation();
	const linkSiteMut = useLinkStationSite();
	const unlinkSiteMut = useUnlinkStationSite();

	const queryManager = useQueryManager();

	return async (actions: StationMutation[]) => {
		return queryManager.chainInvalidation(async () => {
			for (const action of actions) {
				switch (action.type) {
					case "Update":
						await updateMut.mutateAsync({ id: action.id, data: action.data });
						break;

					case "LinkSite":
						await linkSiteMut.mutateAsync({ id: action.id, site: action.site });
						break;

					case "UnlinkSite":
						await unlinkSiteMut.mutateAsync(action.id);
				}
			}
		});
	};
}

// TODO: handle recipients
// export function useStationSubscriptionUpdate(updateId: MaybeRef<string>) {
// 	const api = useApi().station();

// 	return useGenericMutation(
// 		(subscribe: boolean) => {
// 			if (subscribe) {
// 				return api.subscribe(unref(updateId));
// 			} else {
// 				return api.unsubscribe(unref(updateId));
// 			}
// 		},
// 		[STATION_KEY(updateId), ALL_STATIONS_KEY, ORG_STATIONS_KEY, FAVORITE_STATIONS_KEY]
// 	);
// }

export function useDeleteStation() {
	const api = useApi().station();

	return useMutation({
		mutation: (id: string) => api.delete(id),
		onSuccess: useInvalidateListCache(),
	});
}
